import React, { forwardRef, memo, Ref, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ReactSelect, {
  ActionMeta,
  OptionsType,
  OptionTypeBase,
  Props as ReactSelectProps,
  ValueType,
} from 'react-select';
import { Theme } from '@app/theme';
import { createUseStyles, useTheme } from 'react-jss';

import {
  defaultInputStyleFunction,
  GenericInput,
  GenericInputProps,
} from '../GenericInput';
import { ChipColor } from '@app/core';
import { useTrackReactSelect } from './useReactSelectChangeLog';
import { useControlledField } from '../FormikFields';

export const useReactSelectStyle = createUseStyles<
  string,
  {
    small?: boolean;
    isMenuRelative?: boolean;
    opaquePlaceholder?: boolean;
    color?: ChipColor;
  },
  Theme
>((theme: Theme) => {
  return {
    picker: {
      '& .react-select__control': {
        ...defaultInputStyleFunction(theme).input,
        cursor: 'text',
        minHeight: theme.height.regular,
        paddingTop: theme.marginBase / 2,
        paddingBottom: theme.marginBase / 2,
      },
      '&.small .react-select__control': {
        minHeight: theme.height.small,
        paddingTop: 0,
        paddingBottom: 0,
      },
      '& .react-select__input': {
        maxWidth: 400,
      },
      '& .react-select__value-container': {
        padding: 0,
      },
      '& .react-select__indicator': {
        paddingRight: 0,
      },
      '& .react-select__menu': {
        position: ({ isMenuRelative }: { isMenuRelative?: boolean }) =>
          isMenuRelative ? 'relative' : 'absolute',
      },
      '& .react-select__menu-list': {
        paddingTop: theme.marginBase,
        paddingBottom: theme.marginBase,
      },
      '& .react-select__option': {
        ...theme.fonts.caption,
        padding: [theme.marginBase, theme.marginBase * 2],
        minHeight: theme.height.small,
        display: 'flex',
        alignItems: 'center',
        color: theme.colors.inkSecondary,
        '&:hover': {
          backgroundColor: theme.colors.grayBackground,
        },
        [theme.media.mobile]: {
          padding: [theme.marginBase, theme.marginBase * 2],
        },
      },
      '& .react-select__option--is-focused': {
        backgroundColor: theme.colors.grayBackground,
      },
      '& .react-select__option--is-disabled': {
        opacity: 0.5,
      },

      '& .react-select__option--is-selected': {
        color: theme.colors.blueAccessible,
        backgroundColor: theme.colors.white,
      },
      '& .react-select__placeholder': {
        opacity: ({ opaquePlaceholder }: { opaquePlaceholder?: boolean }) =>
          opaquePlaceholder ? 1 : 0.6,
        color: ({ opaquePlaceholder }: { opaquePlaceholder?: boolean }) =>
          opaquePlaceholder
            ? theme.colors.inkSecondary
            : theme.colors.inkTertiary,
      },
      '& .react-select__indicator-separator': {
        display: 'none',
      },
      '& .react-select__multi-value': {
        ...theme.fonts.caption,
        borderRadius: theme.borderRadius.std,
        backgroundColor: ({ color }: { color?: ChipColor }) =>
          color === 'green' ? theme.colors.greenMuted : theme.colors.blueMuted,
        color: theme.colors.inkPrimary,
      },
      '& .react-select__multi-value__label': {
        ...theme.fonts.label,
        color: ({ color }: { color?: ChipColor }) =>
          color === 'green'
            ? theme.colors.greenAccessible
            : theme.colors.blueAccessible,
        paddingLeft: theme.marginBase,
        paddingRight: theme.marginBase,
      },
      '& .react-select__multi-value__remove': {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.colors.redMuted,
        },
      },
    },
  };
});

export type OptionsObject = {
  label: string;
  value: string | number;
  hidden?: boolean;
};

type Option = string | number | OptionsObject;

type FullValueType<IsMulti extends boolean> =
  | string
  | number
  | Array<string | number>
  | ValueType<OptionsObject, IsMulti>;

interface Props<IsMulti extends boolean = false>
  extends Omit<GenericInputProps, 'component'> {
  label?: string | React.ReactNode;
  containerClass?: string;
  name?: string;
  error?: string;
  noMargin?: boolean;
  noBottomMargin?: boolean;
  options?: Array<Option>;
  i18nKeyPrefix?: string;
  placeholder?: string;
  color?: 'blue' | 'green';
  value?: FullValueType<IsMulti>;
  withAutocomplete?: boolean;
  onChange?: (
    value: ValueType<OptionsObject, IsMulti>,
    actionMeta: ActionMeta<OptionsObject>,
    name?: string,
  ) => boolean | void | Promise<void | boolean>;
  children?: (
    option:
      | ValueType<OptionsObject, IsMulti>
      | undefined
      | null
      | Array<ValueType<OptionsObject, IsMulti>>,
  ) => React.ReactNode;
  isMulti?: IsMulti;
  onValueChange?: (value?: FullValueType<IsMulti> | null) => void;
  rightAction?: React.ReactNode;
}

const getOptionProperty = (option: Option, ppt: 'label' | 'value') =>
  typeof option === 'string' || typeof option === 'number'
    ? option
    : option[ppt];

type InnerSelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean = false,
> = Partial<GenericInputProps> &
  ReactSelectProps<OptionType, IsMulti> & { color?: 'blue' | 'green' };

const InnerComponent = memo(
  <OptionType extends OptionTypeBase, IsMulti extends boolean = false>({
    className,
    color,
    trackingName,
    ...props
  }: InnerSelectProps<OptionType, IsMulti>) => {
    const theme: Theme = useTheme();
    const classes: any = useReactSelectStyle({
      theme,
      opaquePlaceholder: props.opaquePlaceholder,
      isMenuRelative: props.isMenuRelative,
      small: props.smallSelect,
      color,
    });
    const onChange = useTrackReactSelect(trackingName, props.onChange);
    return (
      <label>
        <ReactSelect
          {...props}
          classNamePrefix="react-select"
          className={classNames(classes.picker, {
            small: props.small,
          })}
          onChange={onChange}
          disabled={props.disabled}
          isDisabled={props.disabled}
        />
      </label>
    );
  },
);

export type SelectProps<
  IsMulti extends boolean = false,
  Option extends OptionsObject = OptionsObject,
> = Props<IsMulti> & Omit<ReactSelectProps<Option>, 'value'>;

export const Select = forwardRef(
  <IsMulti extends boolean = false>(
    {
      options,
      i18nKeyPrefix,
      name,
      children,
      isMulti,
      placeholder,
      withAutocomplete,
      rightAction,
      ...props
    }: SelectProps<IsMulti>,
    ref: Ref<any>,
  ) => {
    const prefix = i18nKeyPrefix ? i18nKeyPrefix + '.' : '';
    const { t } = useTranslation('common');

    const { value, setValue, onChange } = useControlledField({
      ...props,
      name,
    });

    const _onChange = useCallback(
      async (
        option: ValueType<OptionsObject, IsMulti>,
        action: ActionMeta<OptionsObject>,
      ) => {
        onChange(option, action, name);
        if (Array.isArray(option)) {
          setValue(option.map((o) => o.value));
          return;
        }
        setValue(option ? (option as OptionsObject).value : undefined);
      },
      [onChange, name, setValue, isMulti],
    );

    const optionValues = useMemo(
      () =>
        options
          ? options.filter(Boolean).map((option) => ({
              ...(typeof option === 'object' ? option : { hidden: false }),
              value: getOptionProperty(option, 'value'),
              label: getOptionProperty(option, 'label')
                ? t(`${prefix}${getOptionProperty(option, 'label')}`)
                : '',
            }))
          : [],
      [options, t, prefix],
    );

    const filteredOptions = useMemo(
      () => optionValues.filter((o) => !o.hidden),
      [optionValues],
    );

    const val = useMemo(() => {
      if (isMulti && Array.isArray(value)) {
        return value.reduce((arr, val) => {
          const res = optionValues.find((option) => option.value === val);
          if (res) {
            arr.push(res);
          } else if (withAutocomplete) {
            arr.push(val);
          }
          return arr;
        }, [] as OptionsType<OptionsObject>);
      }
      if (typeof value === 'string' || typeof value === 'number') {
        return (
          (optionValues.find(
            (option) => option.value === value,
          ) as OptionsObject) || null
        );
      }
      return value as ValueType<OptionsObject, IsMulti>;
    }, [isMulti, value, optionValues, props.value]);

    return (
      <React.Fragment>
        <GenericInput
          component={InnerComponent}
          placeholder={placeholder}
          {...props}
          ref={ref}
          onChange={_onChange}
          value={val}
          options={filteredOptions}
          isMulti={isMulti}
          name={name}
          disableLogs
          rightAction={rightAction}
        />
        {children ? children(value) : null}
      </React.Fragment>
    );
  },
);
